import AddSvg from 'assets/fullbody/add.svg'
import FullSvg from 'assets/fullbody/full.svg'
import { clsx } from 'clsx'
import { Img } from 'component/Image'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { FullbodyStore, Item, useFullbody } from 'page/ProfileScreen/FullProfilePage/Store'
import { useRef } from 'react'
import { opt } from 'store/opt'
import { store } from 'store/store'
import css from './ItemCard.module.scss'

type Props = {
  item: Item
}

function dot(full: FullbodyStore, item: Item) {
  const { userPhoto } = store.profile.common
  const selected = full.selected === item

  if (item && selected) return <Icon size={24} name="check_circle" fill />
  if (!item && full.custom && userPhoto.src) return <Icon size={20} name="autorenew" />
  return <Icon size={24} name="circle" fill />
}

function content(full: FullbodyStore, item: Item) {
  const { irnby } = opt
  const { userPhoto } = store.profile.common
  const { many } = full.carousel

  if (item) return <Vertical className={css.image}>
    <Img src={item.src} cover />
  </Vertical>

  if (full.custom && userPhoto.src) {
    return <Space className={css.image}>
      <Img src={userPhoto.src} cover />
    </Space>
  }

  return <Vertical gap={16} tall center>
    {!irnby && <FullSvg className={css.full} />}
    <AddSvg />
    <Vertical gap={8} pad={[0, 16]} center>
      <Typo size={14} semi={!irnby} upper={irnby} center>
        {i18n('UploadFullbodyPhoto')}
      </Typo>
      <Typo size={12} secondary center>
        {!many && i18n('UploadFullLengthPhotoFacingCamera')}
        {many && i18n('UploadFullLengthPhotoFacingCameraOrTemplate')}
      </Typo>
    </Vertical>
  </Vertical>
}

export const ItemCard = observer(({ item }: Props) => {
  const full = useFullbody()
  const { userPhoto } = store.profile.common
  const { src, error } = userPhoto
  const { many } = full.carousel
  const selected = full.selected === item && !!src
  const renew = !item && full.custom && !!src
  const dot_ = many || renew
  const ref = useRef<HTMLDivElement>(null)
  const className = clsx(css.card, opt.square && css.square, !many && css.single, selected && css.selected, error && css.error)

  const scroll = useLatestCallback((smooth: boolean) => {
    setTimeout(() => {
      const behavior = smooth ? 'smooth' : 'instant'
      ref.current?.scrollIntoView({ behavior, block: 'center', inline: 'center' })
    }, 0)
  })

  const onClick = useLatestCallback(() => {
    if (!item) userPhoto.clear()

    full.carousel.item = item
    scroll(true)

    if (item) userPhoto.setAsset(item.src, item.name)
    else store.profile.step = 'help'
  })

  useEffectOnce(() => {
    if (selected) scroll(false)
  })

  return <Vertical ref={ref} className={className} onClick={onClick}>
    {content(full, item)}
    {dot_ && <Space className={css.dot} round center>
      {dot(full, item)}
    </Space>}
  </Vertical>
})
